import { useNavigate } from 'react-router-dom';
import { QrCode, ArrowRight } from 'lucide-react';
import { useQR, QR_PRESETS } from '@/hooks/useQRContext';
import { useToast } from '@/components/common/Toast';
import { Card } from '@/components/common';

const TEMPLATES = [
  {
    id: 'website',
    name: 'Website Link',
    description: 'Link to your website or landing page',
    icon: 'URL',
    preset: 'default',
    category: 'Marketing',
  },
  {
    id: 'social-instagram',
    name: 'Instagram Profile',
    description: 'Link to your Instagram profile',
    icon: 'IG',
    preset: 'social',
    category: 'Social Media',
  },
  {
    id: 'social-twitter',
    name: 'Twitter/X Profile',
    description: 'Link to your Twitter/X profile',
    icon: 'X',
    preset: 'social',
    category: 'Social Media',
  },
  {
    id: 'social-linkedin',
    name: 'LinkedIn Profile',
    description: 'Link to your LinkedIn profile',
    icon: 'IN',
    preset: 'business',
    category: 'Social Media',
  },
  {
    id: 'social-youtube',
    name: 'YouTube Channel',
    description: 'Link to your YouTube channel',
    icon: 'YT',
    preset: 'creative',
    category: 'Social Media',
  },
  {
    id: 'business-card',
    name: 'Business Card',
    description: 'Digital vCard with contact info',
    icon: 'VCF',
    preset: 'business',
    category: 'Business',
  },
  {
    id: 'payment-paypal',
    name: 'PayPal Payment',
    description: 'Accept payments via PayPal',
    icon: 'PAY',
    preset: 'payment',
    category: 'Payments',
  },
  {
    id: 'payment-venmo',
    name: 'Venmo Payment',
    description: 'Accept payments via Venmo',
    icon: 'VEN',
    preset: 'payment',
    category: 'Payments',
  },
  {
    id: 'wifi',
    name: 'WiFi Network',
    description: 'Share WiFi credentials easily',
    icon: 'WIFI',
    preset: 'minimal',
    category: 'Utility',
  },
  {
    id: 'email',
    name: 'Email Contact',
    description: 'Quick email compose link',
    icon: 'MAIL',
    preset: 'business',
    category: 'Contact',
  },
  {
    id: 'phone',
    name: 'Phone Number',
    description: 'Quick dial phone number',
    icon: 'TEL',
    preset: 'minimal',
    category: 'Contact',
  },
  {
    id: 'sms',
    name: 'SMS Message',
    description: 'Pre-filled SMS message',
    icon: 'SMS',
    preset: 'creative',
    category: 'Contact',
  },
  {
    id: 'menu',
    name: 'Restaurant Menu',
    description: 'Link to your digital menu',
    icon: 'MENU',
    preset: 'default',
    category: 'Business',
  },
  {
    id: 'event',
    name: 'Event Ticket',
    description: 'Event registration or ticket',
    icon: 'EVT',
    preset: 'creative',
    category: 'Events',
  },
  {
    id: 'app-ios',
    name: 'App Store',
    description: 'Link to your iOS app',
    icon: 'IOS',
    preset: 'minimal',
    category: 'Apps',
  },
  {
    id: 'app-android',
    name: 'Google Play',
    description: 'Link to your Android app',
    icon: 'GP',
    preset: 'minimal',
    category: 'Apps',
  },
];

function TemplatesPage() {
  const navigate = useNavigate();
  const { applyPreset, updateConfig } = useQR();
  const { info } = useToast();

  const categories = TEMPLATES.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {});

  const handleSelectTemplate = (template) => {
    applyPreset(template.preset);
    updateConfig('data', '');
    info(`${template.name} template applied. Enter your content to generate.`);
    navigate('/generator');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            QR Code Templates
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose a template to get started quickly. Each template comes with
            optimized styling for its use case.
          </p>
        </div>

        {Object.entries(categories).map(([category, templates]) => (
          <div key={category} className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {category}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  hover
                  className="group"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 shrink-0 rounded-lg bg-primary-50 px-2 py-3 text-center text-xs font-bold text-primary-700">
                      {template.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {template.description}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-16">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Style Presets
          </h2>
          <p className="text-gray-600 mb-6">
            Or start with a style preset and customize from there
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(QR_PRESETS).map(([key, preset]) => (
              <Card
                key={key}
                hover
                className="group"
                onClick={() => {
                  applyPreset(key);
                  navigate('/generator');
                }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: preset.config.dotsColor }}
                  >
                    <QrCode
                      className="w-8 h-8"
                      style={{
                        color: preset.config.dotsColor === '#ffffff'
                          ? '#000000'
                          : '#ffffff'
                      }}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {preset.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {preset.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplatesPage;
